import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  activityNodeMapAtom,
  destTreeIdMap,
  flowIdsAtom,
  mappedActivityNodes,
  sourceTreeIdMap,
  verifiedNodeIdsAtom,
} from './atoms'
import React from 'react'
import { Table } from 'shared/ui/Table/Table'
import {
  getNodeRowDataAsString,
  lessonPlanDiffCell,
  openSourceDestCell,
  questionDiffCell,
  titleDiffCell,
  unitDiffCell,
  VerifyCell,
} from './tableColumns'
import { useListSwap } from 'shared/hooks'
import { Button } from '@mui/material'
import { useUpdatesForActivityId } from './hooks'
import { Column } from 'shared/ui/Table/types'
import { openTab } from '../../Helpers'
import { NodeOperationsConfirmationButton } from '../../contentService/NodeOperationsConfirmationButton'
import { OutlinedButton } from 'shared/ui'

export function MappedNodes() {
  const items = useAtomValue(mappedActivityNodes)
  const { toInclude, toExclude, onExclude, onInclude, excludeAll, includeAll } =
    useListSwap({ items })

  const mappedColumns = useIncludedTableColumns(onExclude)
  const excludedColumns = useExcludedTableColumns(onInclude)

  if (!items.length) return null

  return (
    <>
      <Table
        title={`Mapped Activities, Can Update: ${toInclude.length}`}
        toolBarItems={[
          <ExcludeAllVerifiedButton
            onExclude={onExclude}
            key="excludeVerified"
          />,
          <Button key="excludeAll" onClick={excludeAll}>
            Exclude All
          </Button>,
          <UpdateButton key="updateAll" itemId={toInclude}>
            Update All
          </UpdateButton>,
        ]}
        columns={mappedColumns}
        items={toInclude}
        canExport={true}
      />
      <Table
        title={`Excluding From Updating: ${toExclude.length}`}
        toolBarItems={[
          <Button key="includeAll" onClick={includeAll}>
            Include All
          </Button>,
        ]}
        canExport={true}
        columns={excludedColumns}
        items={toExclude}
      />
    </>
  )
}

function useIncludedTableColumns(onExclude: (item: string) => void) {
  const columns = useTreeColumns()

  return [
    ...columns,
    actionColumn('Exclude', onExclude),
    {
      title: 'Update',
      rowContent(item: string) {
        return <UpdateButton itemId={[item]} />
      },
    },
  ]
}

function useExcludedTableColumns(onInclude: (item: string) => void) {
  const columns = useTreeColumns()
  return [...columns, actionColumn('Include', onInclude)]
}

function useTreeColumns(): Column<string>[] {
  const destIdMap = useAtomValue(destTreeIdMap)
  const sourceIdMap = useAtomValue(sourceTreeIdMap)
  const nodeMapping = useAtomValue(activityNodeMapAtom)

  return [
    {
      title: 'Title',
      rowContent: titleDiffCell({ destIdMap, nodeMapping }),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'title',
      }),
    },
    {
      title: 'QCount',
      rowContent: questionDiffCell({ destIdMap, nodeMapping }),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'qcount',
      }),
    },
    {
      title: 'Verify',
      rowContent: (destinationNodeId: string) => (
        <VerifyCell destinationNodeId={destinationNodeId} />
      ),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'verify',
      }),
    },
    {
      title: 'Unit',
      rowContent: unitDiffCell({ destIdMap, sourceIdMap, nodeMapping }),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'unit',
      }),
    },
    {
      title: 'Lesson Plan',
      rowContent: lessonPlanDiffCell({ destIdMap, sourceIdMap, nodeMapping }),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'lesson',
      }),
    },
    {
      title: 'Kiddom',
      rowContent: openSourceDestCell({ destIdMap, sourceIdMap, nodeMapping }),
      rowString: getNodeRowDataAsString({
        destIdMap,
        sourceIdMap,
        nodeMapping,
        dataType: 'url',
      }),
    },
  ]
}

function actionColumn(title: string, onAction: (id: string) => void) {
  return {
    title,
    rowContent(item: string) {
      return (
        <OutlinedButton onClick={() => onAction(item)}>{title}</OutlinedButton>
      )
    },
  }
}

function UpdateButton({
  itemId,
  children = 'Update',
}: {
  itemId: string[]
  children?: string
}) {
  const setFlowIds = useSetAtom(flowIdsAtom)
  const updatesData = useUpdatesForActivityId(...itemId)

  const onFlowCreated = (flowId: string) => {
    setFlowIds((prev) => [flowId, ...prev])
    openTab('/content_service/flow/' + flowId)
  }

  return (
    <NodeOperationsConfirmationButton
      operations={updatesData}
      onFlowCreated={onFlowCreated}
      confTitle={`Update Activity: ${updatesData?.length} Operations`}
    >
      {children}
    </NodeOperationsConfirmationButton>
  )
}

function ExcludeAllVerifiedButton({
  onExclude,
}: {
  onExclude: (...id: string[]) => void
}) {
  const verifiedNodes = useAtomValue(verifiedNodeIdsAtom)

  const doExclude = () => {
    onExclude(...verifiedNodes)
  }

  return (
    <Button disabled={!verifiedNodes.length} onClick={doExclude}>
      Exclude Verified
    </Button>
  )
}
